import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TeacherAuthProvider, useTeacherAuth } from './context/TeacherAuthContext';
import { ChatProvider } from './context/ChatContext';
import Landing from './components/Landing';
import Login from './components/Login';
import TeacherAuth from './components/TeacherAuth';
import ChatInterface from './components/ChatInterface';
import PromptVersionsView from './components/PromptVersionsView';
import TeacherView from './components/TeacherView';
import ClassDetails from './components/ClassDetails';
import ClassSummary from './components/ClassSummary';
import ClassReinforcement from './components/ClassReinforcement';
import ClassWeeklyPlan from './components/ClassWeeklyPlan';
import TeacherDashboard from './components/TeacherDashboard';
import AskCoach from './components/teacher/AskCoach';
import TeacherConversationView from './components/TeacherConversationView';
import StudentReport from './components/StudentReport';
import ClassReport from './components/ClassReport';
import AdminLogin from './components/admin/AdminLogin';
import MonitorDashboard from './components/admin/MonitorDashboard';
import FacilitatorDirectory from './components/admin/FacilitatorDirectory';
import FacilitatorSchools from './components/admin/FacilitatorSchools';
import VacationProjects from './components/admin/VacationProjects';
import FacilitatorDetail from './components/admin/FacilitatorDetail';
import SuperAdminOverview from './components/super-admin/SuperAdminOverview';
import SuperAdminSchools from './components/super-admin/SuperAdminSchools';
import SuperAdminSchoolClasses from './components/super-admin/SuperAdminSchoolClasses';
import SuperAdminClass from './components/super-admin/SuperAdminClass';
import SuperAdminStudent from './components/super-admin/SuperAdminStudent';
import SuperAdminFacilitators from './components/super-admin/SuperAdminFacilitators';
import SuperAdminFacilitatorDetail from './components/super-admin/SuperAdminFacilitatorDetail';
import SuperAdminStudentGroups from './components/super-admin/SuperAdminStudentGroups';
import SuperAdminLogin from './components/super-admin/SuperAdminLogin';
import SuperAdminInternalValidation from './components/super-admin/SuperAdminInternalValidation';
import PdfTopicExtractor from './components/PdfTopicExtractor';
import AssignedTopic from './components/AssignedTopic';
import QuizPage from './components/QuizPage';
import AssessmentsPage from './components/AssessmentsPage';
import EntryAssessmentPage from './components/EntryAssessmentPage';
import TeacherAssessments from './components/TeacherAssessments';
import MyHomework from './components/MyHomework';
import MyProjects from './components/MyProjects';
import ClassHomework from './components/teacher/ClassHomework';

// Protected route component (student / logged-in users)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  if (!user) {
    // Send unauthenticated visitors to the student login, preserving query params.
    const targetPath = location.search ? `/login${location.search}` : '/login';
    return <Navigate to={targetPath} replace />;
  }

  // Backfilled / temp-password accounts must reset before reaching chat.
  // The /login screen renders the force-reset UI when this flag is true.
  if (user.must_reset_password) {
    return <Navigate to="/login" replace />;
  }

  // The one-time entry assessment comes before everything else a student can
  // reach, including chat. The backend reports the flag on login and /me.
  if (user.entry_assessment_pending && location.pathname !== '/entry-assessment') {
    return <Navigate to="/entry-assessment" replace />;
  }

  return <>{children}</>;
};

// Gate for teacher dashboards — requires a logged-in teacher (username + password).
const TeacherRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { teacher, token, isLoading } = useTeacherAuth();

  if (isLoading) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  if (!teacher || !token) {
    return <Navigate to="/teacher-login" replace />;
  }

  return <>{children}</>;
};

// Gate for the facilitator MONITORING dashboard — shared-secret monitor token.
// A separate surface from the /admin DB browser, with its own credentials.
const MonitorRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!localStorage.getItem('monitorToken')) {
    return <Navigate to="/monitor/login" replace />;
  }
  return <>{children}</>;
};

const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!localStorage.getItem('superAdminToken')) {
    return <Navigate to="/super-admin/login" replace />;
  }
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <TeacherAuthProvider>
        <Router>
          <ChatProvider>
            <div className="min-h-screen bg-gray-50">
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/try" element={<Login />} />
                <Route path="/teacher-login" element={<TeacherAuth />} />
                <Route path="/monitor/login" element={<AdminLogin />} />
                <Route path="/super-admin/login" element={<SuperAdminLogin />} />
                <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminOverview /></SuperAdminRoute>} />
                <Route path="/super-admin/facilitators" element={<SuperAdminRoute><SuperAdminFacilitators /></SuperAdminRoute>} />
                <Route path="/super-admin/facilitators/:id" element={<SuperAdminRoute><SuperAdminFacilitatorDetail /></SuperAdminRoute>} />
                <Route path="/super-admin/student-groups" element={<SuperAdminRoute><SuperAdminStudentGroups /></SuperAdminRoute>} />
                <Route path="/super-admin/internal-validation" element={<SuperAdminRoute><SuperAdminInternalValidation /></SuperAdminRoute>} />
                <Route path="/super-admin/schools" element={<SuperAdminRoute><SuperAdminSchools /></SuperAdminRoute>} />
                <Route path="/super-admin/schools/:school" element={<SuperAdminRoute><SuperAdminSchoolClasses /></SuperAdminRoute>} />
                <Route path="/super-admin/class" element={<SuperAdminRoute><SuperAdminClass /></SuperAdminRoute>} />
                <Route path="/super-admin/students/:id" element={<SuperAdminRoute><SuperAdminStudent /></SuperAdminRoute>} />
                <Route path="/monitor" element={<MonitorRoute><MonitorDashboard /></MonitorRoute>} />
                <Route path="/monitor/facilitators" element={<MonitorRoute><FacilitatorDirectory /></MonitorRoute>} />
                <Route path="/monitor/facilitators/:id" element={<MonitorRoute><FacilitatorSchools /></MonitorRoute>} />
                <Route path="/monitor/facilitators/:id/detail" element={<MonitorRoute><FacilitatorDetail /></MonitorRoute>} />
                <Route path="/monitor/vacation" element={<MonitorRoute><VacationProjects /></MonitorRoute>} />
                {/* Legacy school-grouped routes now redirect to the facilitator directory. */}
                <Route path="/monitor/schools" element={<Navigate to="/monitor/facilitators" replace />} />
                <Route path="/monitor/schools/:school" element={<Navigate to="/monitor/facilitators" replace />} />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <ChatInterface mode="chat" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/test-prompt"
                element={
                  <ProtectedRoute>
                    <ChatInterface mode="test-prompt" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/prompts"
                element={
                  <ProtectedRoute>
                    <PromptVersionsView />
                  </ProtectedRoute>
                }
              />
              <Route path="/teacher-view" element={<TeacherRoute><TeacherView /></TeacherRoute>} />
              <Route path="/class-details" element={<TeacherRoute><ClassDetails /></TeacherRoute>} />
              <Route path="/class-summary" element={<TeacherRoute><ClassSummary /></TeacherRoute>} />
              <Route path="/class-reinforcement" element={<TeacherRoute><ClassReinforcement /></TeacherRoute>} />
              <Route path="/class-weekly-plan" element={<TeacherRoute><ClassWeeklyPlan /></TeacherRoute>} />
              <Route path="/ask-coach" element={<TeacherRoute><AskCoach /></TeacherRoute>} />
              <Route path="/teacher-dashboard" element={<TeacherRoute><TeacherDashboard /></TeacherRoute>} />
              <Route path="/class-conversation" element={<TeacherRoute><TeacherConversationView /></TeacherRoute>} />
              {/* Student Analysis merged into Student Report — both routes render it. */}
              <Route path="/student-analysis" element={<TeacherRoute><StudentReport /></TeacherRoute>} />
              <Route path="/student-report" element={<TeacherRoute><StudentReport /></TeacherRoute>} />
              <Route path="/class-report" element={<TeacherRoute><ClassReport /></TeacherRoute>} />
              <Route path="/pdf-topics" element={<TeacherRoute><PdfTopicExtractor /></TeacherRoute>} />
              <Route path="/teacher-assessments" element={<TeacherRoute><TeacherAssessments /></TeacherRoute>} />
              <Route path="/class-homework" element={<TeacherRoute><ClassHomework /></TeacherRoute>} />
              <Route path="/topic/:id" element={<ProtectedRoute><AssignedTopic /></ProtectedRoute>} />
              <Route path="/quiz/:conversationId" element={<ProtectedRoute><QuizPage /></ProtectedRoute>} />
              <Route path="/assessments" element={<ProtectedRoute><AssessmentsPage /></ProtectedRoute>} />
              <Route path="/entry-assessment" element={<ProtectedRoute><EntryAssessmentPage /></ProtectedRoute>} />
              <Route path="/homework" element={<ProtectedRoute><MyHomework /></ProtectedRoute>} />
              <Route path="/projects" element={<ProtectedRoute><MyProjects /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </ChatProvider>
        </Router>
      </TeacherAuthProvider>
    </AuthProvider>
  );
};

export default App;
